import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Calendar, TrendingUp, Clock, AlertTriangle } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { KpiCard } from '@/components/dashboard/kpi-card';
import { ChartCard } from '@/components/dashboard/chart-card';
import { DepartmentDonut } from '@/components/dashboard/dept-donut';
import { SpendingChart } from '@/components/dashboard/spending-chart';
import { AlertHeatmap } from '@/components/dashboard/alert-heatmap';
import { RecentTransactions } from '@/components/dashboard/recent-transactions';
import { TopMerchants } from '@/components/dashboard/top-merchants';
import { RecentAlerts } from '@/components/dashboard/recent-alerts';
import { DashboardFilters } from '../components/DashboardFilters';
import { api } from '../lib/api';
import { formatVnd } from '../lib/format';
import { Skeleton } from '@/components/ui/skeleton';

function buildTrend(
  current: number,
  prev: number,
): { value: number; direction: 'up' | 'down' | 'flat' } | undefined {
  if (prev === 0) return undefined; // hide chip — no meaningful comparison
  const pct = Math.round(((current - prev) / prev) * 100);
  const direction = pct >= 1 ? 'up' : pct <= -1 ? 'down' : 'flat';
  return { value: pct, direction };
}

function buildLink(path: string, params: Record<string, string | undefined>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) qs.set(k, v);
  const s = qs.toString();
  return s ? `${path}?${s}` : path;
}

export function HomePage() {
  const [searchParams] = useSearchParams();
  const department = searchParams.get('department') || undefined;
  const rangeAll = searchParams.get('range') === 'all';
  const dateFrom = searchParams.get('date_from') || undefined;
  const dateTo = searchParams.get('date_to') || undefined;

  const { data, isLoading } = useQuery({
    queryKey: ['kpis', department ?? null, rangeAll ? 'all' : (dateFrom ?? null), dateTo ?? null],
    queryFn: () => api.getKpis({ department, date_from: dateFrom, date_to: dateTo, all: rangeAll }),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  // Effective range for KPI deep-links into /transactions. Empty URL on the
  // dashboard means "this month", so links carry explicit this-month bounds.
  const todayStr = new Date().toISOString().slice(0, 10);
  const monthStartStr = todayStr.slice(0, 7) + '-01';
  const effFrom = rangeAll ? undefined : (dateFrom ?? monthStartStr);
  const effTo = rangeAll ? undefined : (dateTo ?? todayStr);

  const txHref = buildLink('/transactions', {
    date_from: effFrom,
    date_to: effTo,
    department,
  });
  const pendingHref = buildLink('/pending', { department });
  const alertsHref = buildLink('/alerts', { severity: 'high', department });

  const totalTrend = data ? buildTrend(data.total_vnd, data.prev_total_vnd) : undefined;
  const countTrend = data ? buildTrend(data.tx_count, data.prev_tx_count) : undefined;
  const pendingTrend = data ? buildTrend(data.pending_count, data.prev_pending_count) : undefined;
  const highTrend = data ? buildTrend(data.high_count, data.prev_high_count) : undefined;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        subtitle="Tổng quan giao dịch và cảnh báo trên các thẻ doanh nghiệp."
      />

      <DashboardFilters />

      {/* Row 1 — KPI */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {isLoading ? (
          <>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </>
        ) : (
          <>
            <KpiCard
              label="Tổng chi tiêu"
              value={data ? formatVnd(data.total_vnd) : '—'}
              trend={totalTrend}
              icon={TrendingUp}
              href={txHref}
            />
            <KpiCard
              label="Số giao dịch"
              value={data ? String(data.tx_count) : '—'}
              trend={countTrend}
              icon={Calendar}
              href={txHref}
            />
            <KpiCard
              label="Pending"
              value={data ? String(data.pending_count) : '—'}
              trend={pendingTrend}
              icon={Clock}
              variant={data?.pending_count && data.pending_count > 0 ? 'warning' : 'default'}
              href={pendingHref}
            />
            <KpiCard
              label="High Alerts"
              value={data ? String(data.high_count) : '—'}
              trend={highTrend}
              icon={AlertTriangle}
              variant={data?.high_count && data.high_count > 0 ? 'destructive' : 'default'}
              href={alertsHref}
            />
          </>
        )}
      </div>

      {/* Row 2 — Spending + Donut */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <SpendingChart department={department} />
        </div>
        <div>
          <DepartmentDonut dateFrom={dateFrom} dateTo={dateTo} all={rangeAll} />
        </div>
      </div>

      {/* Row 3 — Top merchants + Recent alerts + Heatmap */}
      <div className="grid gap-4 lg:grid-cols-3">
        <TopMerchants department={department} dateFrom={dateFrom} dateTo={dateTo} all={rangeAll} />
        <RecentAlerts department={department} />
        <AlertHeatmap department={department} />
      </div>

      {/* Row 4 — Top transactions full width */}
      <ChartCard
        title="Top 10 giao dịch lớn nhất"
        description="Trong kỳ đang lọc · sắp xếp theo số tiền giảm dần"
      >
        <RecentTransactions department={department} dateFrom={effFrom} dateTo={effTo} />
      </ChartCard>
    </div>
  );
}
